import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect } from "react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { ApolloError, useLazyQuery } from "@apollo/client"
import { computeActivityStatus, getNextAction } from "../utils/activities/activityStatus"
import { BatchItem } from "../components/activities/inputs/BatchCombobox"

interface ActivityFormData {
  kind: gqlTypes.ActivityKind
  name?: string
  start?: string
  end?: string
  asset?: string
}

export interface Link {
  targetActivityId: string
  linkType: "source" | "destination"
  siteId?: string
}

export interface LinkableActivitiesCache {
  before?: gqlTypes.LinkableActivity
  after?: gqlTypes.LinkableActivity
  // Cache key to know when to invalidate (based on asset + dates)
  cacheKey?: string
}

export interface AttributeValues {
  pressure: string
  quantity: string
  temperature: string
  fill_level: string
  asset: string | null
  site: string | null
  batch: string | null
  source: string | null
  destination: string | null
  note: string
  [key: string]: string | null
}

export interface CreateFormData {
  kind: gqlTypes.ActivityKind
  status: gqlTypes.ActivityEventKind
  order?: gqlTypes.Order
  start?: string
  end?: string
  attributes: AttributeValues
  links: Link[]
  availableAssets?: gqlTypes.AssetBasic[]
  quantity_scheduled?: string
}

export type RefreshMethod = "silent" | "normal"

export interface ActivityWorkflowState {
  // Mode
  mode: "create" | "edit"

  // Dialog state
  dialogOpen: boolean
  currentView: "main" | "edit-attributes" | "progress-status" | "confirm-delete" | "create-batch" | "edit-details"

  // Activity being edited (if any)
  currentActivityId?: string
  activity?: gqlTypes.Activity
  activities?: gqlTypes.Activity[]
  asset?: gqlTypes.Asset

  latestBatch?: BatchItem

  currentFormData?: ActivityFormData
  currentEventKind?: gqlTypes.ActivityEventKind

  // Creation state
  createFormData?: CreateFormData
  isCreating: boolean
  linkableActivitiesCache?: LinkableActivitiesCache
  availabilityWarning?: string | null

  // Linked site defaults (populated when linking activities in edit mode)
  // undefined = no pending change, null = explicitly clear, string = set to this site
  linkedSiteDefaults?: { source?: string | null; destination?: string | null }

  // UI state
  isSubmitting: boolean
  isDeleting: boolean

  // For batch creation flow
  returnToView?: "main" | "edit-attributes" | "progress-status"

  isStatusProgression?: boolean
  needToRefresh?: RefreshMethod

  // State
  loading: boolean
  error: ApolloError | undefined
}

interface ActivityWorkflowActions {
  // Dialog actions
  openDialog: (activityId?: string) => void
  closeDialog: () => void
  setView: (view: ActivityWorkflowState["currentView"]) => void

  // Activity actions
  setActivity: (activity: gqlTypes.Activity) => void
  setActivities: (activities: gqlTypes.Activity[]) => void
  setCurrentEventKind: (eventKind: gqlTypes.ActivityEventKind) => void
  setLatestBatch: (batch: BatchItem) => void

  // Asset actions
  setAsset: (asset: gqlTypes.Asset) => void

  // Form actions
  setActivityKind: (kind: gqlTypes.ActivityKind) => void
  setStartDateTime: (dateTime: string) => void
  setEndDateTime: (dateTime: string) => void
  setSelectedAsset: (assetId: string) => void
  initialiseCreateFormData: (activityKinds?: gqlTypes.ActivityKind[]) => void

  // Creation actions
  setMode: (mode: "create" | "edit") => void
  setCreateFormData: (data: CreateFormData) => void
  updateCreateFormField: <K extends keyof CreateFormData>(field: K, value: CreateFormData[K]) => void
  resetCreateForm: (activityKinds?: gqlTypes.ActivityKind[]) => void
  setIsCreating: (isCreating: boolean) => void
  setLinkableActivitiesCache: (cache: LinkableActivitiesCache | undefined) => void
  setAvailabilityWarning: (warning: string | null) => void

  // Submission state
  setSubmitting: (submitting: boolean) => void
  setDeleting: (deleting: boolean) => void

  // Navigation
  navigateToBatchCreation: (returnTo: ActivityWorkflowState["returnToView"]) => void
  navigateToAttributeEdit: (eventKind: gqlTypes.ActivityEventKind, isProgression: boolean) => void
  navigateToDetailsEdit: () => void
  navigateToDeleteConfirm: () => void
  returnToMain: () => void

  // Reset
  resetWorkflow: () => void

  // Refresh
  needToRefresh: (method?: RefreshMethod) => void
  hasRefreshed: () => void

  // Linked site defaults
  setLinkedSiteDefaults: (defaults: { source?: string | null; destination?: string | null }) => void
}

// Split into two contexts to prevent unnecessary re-renders
const ActivityWorkflowStateContext = createContext<ActivityWorkflowState | undefined>(undefined)
const ActivityWorkflowActionsContext = createContext<ActivityWorkflowActions | undefined>(undefined)

const getInitialCreateFormData = (activityKinds?: gqlTypes.ActivityKind[]): CreateFormData => ({
  kind: activityKinds?.[0] || gqlTypes.ActivityKind.Filling,
  status: gqlTypes.ActivityEventKind.Scheduled,
  order: undefined,
  start: undefined,
  end: undefined,
  attributes: {
    pressure: "",
    quantity: "",
    temperature: "",
    fill_level: "",
    asset: null,
    site: null,
    batch: null,
    source: null,
    destination: null,
    note: "",
  },
  links: [],
  availableAssets: undefined,
  quantity_scheduled: undefined,
})

const initialState: ActivityWorkflowState = {
  mode: "edit",
  dialogOpen: false,
  currentView: "main",
  activity: undefined,
  currentFormData: undefined,
  isSubmitting: false,
  isDeleting: false,
  returnToView: undefined,
  isStatusProgression: false,
  needToRefresh: undefined,
  loading: false,
  error: undefined,
  latestBatch: undefined,
  asset: undefined,
  createFormData: undefined,
  isCreating: false,
  linkableActivitiesCache: undefined,
  linkedSiteDefaults: undefined,
}

export const ActivityWorkflowProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ActivityWorkflowState>(initialState)

  // Dialog actions
  const openDialog = useCallback((activityId?: string, activity?: gqlTypes.Activity) => {
    setState((prev) => ({
      ...prev,
      dialogOpen: true,
      currentActivityId: activityId,
      currentActivity: activity,
      currentView: "main",
      isSubmitting: false,
      isDeleting: false,
      createFormData: getInitialCreateFormData(activity?.kind ? [activity.kind] : undefined),
    }))
  }, [])

  const closeDialog = useCallback(() => {
    setState((prev) => ({
      ...prev,
      dialogOpen: false,
      // Don't reset other state immediately - let it clean up after animation
    }))
  }, [])

  const setView = useCallback((view: ActivityWorkflowState["currentView"]) => {
    setState((prev) => ({
      ...prev,
      currentView: view,
    }))
  }, [])

  // Form actions
  const setActivityKind = useCallback((kind: gqlTypes.ActivityKind) => {
    setState((prev) => ({
      ...prev,
      activityKind: kind,
    }))
  }, [])

  const setStartDateTime = useCallback((dateTime: string) => {
    setState((prev) => ({
      ...prev,
      startDateTime: dateTime,
    }))
  }, [])

  const setEndDateTime = useCallback((dateTime: string) => {
    setState((prev) => ({
      ...prev,
      endDateTime: dateTime,
    }))
  }, [])

  const setSelectedAsset = useCallback((assetId: string) => {
    setState((prev) => ({
      ...prev,
      selectedAsset: assetId,
    }))
  }, [])

  // Submission state
  const setSubmitting = useCallback((submitting: boolean) => {
    setState((prev) => ({
      ...prev,
      isSubmitting: submitting,
    }))
  }, [])

  const setDeleting = useCallback((deleting: boolean) => {
    setState((prev) => ({
      ...prev,
      isDeleting: deleting,
    }))
  }, [])

  // Navigation
  const navigateToBatchCreation = useCallback((returnTo: ActivityWorkflowState["returnToView"]) => {
    setState((prev) => ({
      ...prev,
      currentView: "create-batch",
      returnToView: returnTo,
    }))
  }, [])

  const navigateToAttributeEdit = useCallback((eventKind: gqlTypes.ActivityEventKind, isProgression: boolean) => {
    setState((prev) => ({
      ...prev,
      currentView: "edit-attributes",
      currentEventKind: eventKind,
      isStatusProgression: isProgression,
    }))
  }, [])

  const navigateToDetailsEdit = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentView: "edit-details",
    }))
  }, [])

  const navigateToDeleteConfirm = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentView: "confirm-delete",
    }))
  }, [])

  const returnToMain = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentView: "main",
      currentEventKind: undefined,
      isStatusProgression: false,
    }))
  }, [])

  // Reset workflow to initial state
  const resetWorkflow = useCallback(() => {
    setState(initialState)
  }, [])

  const setActivity = useCallback((activity: gqlTypes.Activity) => {
    setState((prev) => ({
      ...prev,
      activity: activity,
    }))
  }, [])

  const setActivities = useCallback((activities: gqlTypes.Activity[]) => {
    setState((prev) => ({
      ...prev,
      activities: activities,
    }))
  }, [])

  const setCurrentEventKind = useCallback((eventKind: gqlTypes.ActivityEventKind) => {
    setState((prev) => ({
      ...prev,
      currentEventKind: eventKind,
    }))
  }, [])

  const needToRefresh = useCallback((method?: RefreshMethod) => {
    setState((prev) => ({
      ...prev,
      needToRefresh: method ?? "normal",
    }))
  }, [])

  const hasRefreshed = useCallback(() => {
    setState((prev) => ({
      ...prev,
      needToRefresh: undefined,
    }))
  }, [])

  const setLinkedSiteDefaults = useCallback((defaults: { source?: string | null; destination?: string | null }) => {
    setState((prev) => ({
      ...prev,
      linkedSiteDefaults: {
        ...prev.linkedSiteDefaults,
        ...defaults,
      },
    }))
  }, [])

  const setLatestBatch = useCallback((batch: BatchItem) => {
    setState((prev) => ({
      ...prev,
      latestBatch: batch,
    }))
  }, [])

  const setAsset = useCallback((asset: gqlTypes.Asset) => {
    setState((prev) => ({
      ...prev,
      asset: asset,
    }))
  }, [])

  // Creation actions
  const setMode = useCallback((mode: "create" | "edit") => {
    setState((prev) => ({
      ...prev,
      mode,
    }))
  }, [])

  const setCreateFormData = useCallback((data: CreateFormData) => {
    setState((prev) => ({
      ...prev,
      createFormData: data,
    }))
  }, [])

  const updateCreateFormField = useCallback(<K extends keyof CreateFormData>(field: K, value: CreateFormData[K]) => {
    setState((prev) => ({
      ...prev,
      createFormData: prev.createFormData
        ? {
            ...prev.createFormData,
            [field]: value,
          }
        : undefined,
    }))
  }, [])

  const resetCreateForm = useCallback((activityKinds?: gqlTypes.ActivityKind[]) => {
    setState((prev) => ({
      ...prev,
      createFormData: getInitialCreateFormData(activityKinds),
      availabilityWarning: null,
    }))
  }, [])

  const setIsCreating = useCallback((isCreating: boolean) => {
    setState((prev) => ({
      ...prev,
      isCreating,
    }))
  }, [])

  const initialiseCreateFormData = useCallback((activityKinds?: gqlTypes.ActivityKind[]) => {
    setState((prev) => ({
      ...prev,
      createFormData: getInitialCreateFormData(activityKinds),
      availabilityWarning: null,
    }))
  }, [])

  const setLinkableActivitiesCache = useCallback((cache: LinkableActivitiesCache | undefined) => {
    setState((prev) => ({
      ...prev,
      linkableActivitiesCache: cache,
    }))
  }, [])

  const setAvailabilityWarning = useCallback((warning: string | null) => {
    setState((prev) => ({
      ...prev,
      availabilityWarning: warning,
    }))
  }, [])

  // Memoize actions - these never change since callbacks are truly stable
  const actions: ActivityWorkflowActions = useMemo(
    () => ({
      openDialog,
      closeDialog,
      setView,
      setActivity,
      setActivities,
      setActivityKind,
      needToRefresh,
      hasRefreshed,
      setStartDateTime,
      setEndDateTime,
      setSelectedAsset,
      setCurrentEventKind,
      setSubmitting,
      setDeleting,
      navigateToBatchCreation,
      navigateToAttributeEdit,
      navigateToDetailsEdit,
      navigateToDeleteConfirm,
      returnToMain,
      resetWorkflow,
      setLatestBatch,
      setAsset,
      setMode,
      setCreateFormData,
      updateCreateFormField,
      resetCreateForm,
      setIsCreating,
      initialiseCreateFormData,
      setLinkableActivitiesCache,
      setAvailabilityWarning,
      setLinkedSiteDefaults,
    }),
    [] // Empty array - callbacks are stable with no dependencies
  )

  return (
    <ActivityWorkflowStateContext.Provider value={state}>
      <ActivityWorkflowActionsContext.Provider value={actions}>{children}</ActivityWorkflowActionsContext.Provider>
    </ActivityWorkflowStateContext.Provider>
  )
}

// Hook to access state only (will re-render when state changes)
// Returns default values if no provider exists (for withDialog=false usage)
export const useActivityWorkflowState = () => {
  const context = useContext(ActivityWorkflowStateContext)
  if (context === undefined) {
    // Return safe defaults when no provider exists
    return initialState
  }
  return context
}

// Hook to access actions only (will NOT re-render when state changes)
// Returns no-op functions if no provider exists (for withDialog=false usage)
export const useActivityWorkflowActions = () => {
  const context = useContext(ActivityWorkflowActionsContext)
  if (context === undefined) {
    // Return no-op actions when no provider exists
    return {
      openDialog: () => {},
      closeDialog: () => {},
      setView: () => {},
      setActivity: () => {},
      setActivities: () => {},
      setActivityKind: () => {},
      setStartDateTime: () => {},
      needToRefresh: () => {},
      hasRefreshed: () => {},
      setEndDateTime: () => {},
      setSelectedAsset: () => {},
      setCurrentEventKind: () => {},
      setSubmitting: () => {},
      setDeleting: () => {},
      setLatestBatch: () => {},
      navigateToBatchCreation: () => {},
      navigateToAttributeEdit: () => {},
      navigateToDetailsEdit: () => {},
      navigateToDeleteConfirm: () => {},
      returnToMain: () => {},
      resetWorkflow: () => {},
      setAsset: () => {},
      setMode: () => {},
      setCreateFormData: () => {},
      updateCreateFormField: () => {},
      resetCreateForm: () => {},
      setIsCreating: () => {},
      initialiseCreateFormData: () => {},
      setLinkableActivitiesCache: () => {},
      setAvailabilityWarning: () => {},
      setLinkedSiteDefaults: () => {},
    }
  }
  return context
}

// Convenience hook to access both (for backward compatibility)
export const useActivityWorkflow = () => {
  const state = useActivityWorkflowState()
  const actions = useActivityWorkflowActions()
  return { state, ...actions }
}

// Selector hooks for specific parts of state (prevents unnecessary re-renders)
export const useActivityDialogState = () => {
  const state = useActivityWorkflowState()
  return {
    open: state.dialogOpen,
    view: state.currentView,
    isSubmitting: state.isSubmitting,
    isDeleting: state.isDeleting,
  }
}

export const useCurrentActivity = () => {
  const state = useActivityWorkflowState()
  return state.activity
}

export const useCurrentActivities = () => {
  const state = useActivityWorkflowState()
  return state.activities
}

export const useNextEventKind = () => {
  const activity = useCurrentActivity()
  const defaultKind = gqlTypes.ActivityEventKind.Scheduled
  const nextEventKind = activity ? getNextAction(computeActivityStatus(activity)).nextEventKind : defaultKind
  return nextEventKind ?? defaultKind
}

export const useCurrentActivityStatus = () => {
  const activity = useCurrentActivity()
  return activity ? computeActivityStatus(activity) : "Pending"
}

export const useCurrentEventKind = () => {
  const state = useActivityWorkflowState()
  return state.currentEventKind
}

export const useNeedToRefresh = () => {
  const state = useActivityWorkflowState()
  return state.needToRefresh
}

export const useLatestBatch = () => {
  const state = useActivityWorkflowState()
  return state.latestBatch
}

export const useCurrentAsset = () => {
  const state = useActivityWorkflowState()
  return state.asset
}

export const useCreateFormData = () => {
  const state = useActivityWorkflowState()
  return state.createFormData
}

export const useIsCreating = () => {
  const state = useActivityWorkflowState()
  return state.isCreating
}

export const useWorkflowMode = () => {
  const state = useActivityWorkflowState()
  return state.mode
}

export const useLinkableActivitiesCache = () => {
  const state = useActivityWorkflowState()
  return state.linkableActivitiesCache
}

export const useAvailabilityWarning = () => {
  const state = useActivityWorkflowState()
  return state.availabilityWarning
}

export const useLinkedSiteDefaults = () => {
  const state = useActivityWorkflowState()
  return state.linkedSiteDefaults
}
